<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/store/user';
import { deepParseDates, formatDate } from '@/utils/date-utils';
import { useQuery } from '@vue/apollo-composable';
import { OrganizationsDocument, OrganizationType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import OrganizationForm from '@/components/organization/OrganizationForm.vue';
import { ORGANIZATION_USER_ROLES, PAGING_ITEMS_PER_PAGE } from '@/constants';

// user store to get user ID or token-based identity
const userStore = useUserStore();

// Filtering and paging state
const namePattern = ref('');
const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return organizationsQueryResult.value?.organizations?.total || 0;
});

// New organization form state
const showNewOrgForm = ref(false);
const showModalOrgDetail = ref(false);
const showModalOrgUpdate = ref(false);
const selectedOrg = ref<OrganizationType | null>(null);

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: false,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'ownerFullName',
    label: 'Organization owner',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'ownerEmail',
    label: 'Organization owner email',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'addressCountryCode',
    label: 'Country',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'salesforceId',
    label: 'Salesforce ID',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'id',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

// --- Query Organizations ---
const {
  result: organizationsQueryResult,
  loading: organizationsQueryInProgress,
  error: organizationsQueryError,
  refetch: refetchOrganizations,
} = useQuery(OrganizationsDocument, {
  namePattern: namePattern.value,
  page: pagingPageCurrent.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

const organizations = computed<OrganizationType[]>(() => {
  return (
    organizationsQueryResult.value?.organizations?.organizations
      ?.filter((org) => org && org !== null)
      .map((org) => ({ ...(deepParseDates(org) as OrganizationType) })) || []
  );
});

function organizationOwnerEmail(org: OrganizationType) {
  const ownerAssoc = org?.usersAssociated?.find(
    (assoc) =>
      assoc?.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc?.removedAt == null
  );
  return ownerAssoc?.user?.email ?? '';
}

function organizationOwnerFullName(org: OrganizationType) {
  const ownerAssoc = org?.usersAssociated?.find(
    (assoc) =>
      assoc?.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc?.removedAt == null
  );
  return `${ownerAssoc?.user?.givenName || ''} ${
    ownerAssoc?.user?.familyName || ''
  }`.trim();
}

function clearFilter() {
  namePattern.value = '';
  pagingPageCurrent.value = 1;

  requestRefetchOrganizations();
}

// --- Handlers ---
function orgDetailRequested(org: OrganizationType) {
  console.debug('Organization detail requested:', org.id);
  selectedOrg.value = org;
  showModalOrgDetail.value = true;
}

function orgUpdateRequested(org: OrganizationType) {
  console.debug('Organization update requested:', org.id);
  selectedOrg.value = org;
  showModalOrgUpdate.value = true;
}

function onOrganizationCreated() {
  showNewOrgForm.value = false;
  requestRefetchOrganizations();
}

function onOrganizationUpdated() {
  closeModals();
  requestRefetchOrganizations();
}

function closeModals() {
  selectedOrg.value = null;
  showModalOrgDetail.value = false;
  showModalOrgUpdate.value = false;
}

// Functions to refetch data
function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchOrganizations();
  }
}

function requestRefetchOrganizations() {
  refetchOrganizations({
    namePattern: namePattern.value,
    page: pagingPageCurrent.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.then((res) => {
      if (res.data && res.data.organizations) {
        console.debug('Refetch successful:', res.data.organizations);
      } else {
        console.error('Refetch failed, missing data:', res.data);
      }
    })
    .catch((err) => {
      console.error('Refetch failed:', err);
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchOrganizations();
});
</script>

<template>
  <div>
    <h1><font-awesome-icon :icon="['fas', 'building']" /> Organizations</h1>
    <h5>Browse organizations</h5>
    <hr />
    <p>
      Here you can browse the list of organizations and their owners. You can
      also create a new organization.
    </p>
    <!-- Content -->
    <div>
      <!-- Create New Organization -->
      <div class="mb-4">
        <b-button
          @click="showNewOrgForm = !showNewOrgForm"
          variant="outline-dark"
          class="mb-2"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
          <span class="me-2">New organization</span>
          <font-awesome-icon
            v-if="showNewOrgForm"
            :icon="['fas', 'angle-up']"
          />
          <font-awesome-icon v-else :icon="['fas', 'angle-down']" />
        </b-button>
        <b-collapse v-model="showNewOrgForm" id="new-upload">
          <BCard>
            <OrganizationForm
              :headerText="'Create New Organization'"
              :editable="true"
              :buttonText="'Create New Organization'"
              :showAdminFields="userStore.admin"
              @organizationCreated="onOrganizationCreated"
            />
          </BCard>
        </b-collapse>
      </div>

      <h2>
        <font-awesome-icon :icon="['fas', 'building']" class="me-2" />
        List of Organizations
      </h2>
      <!-- Query Error -->
      <b-alert
        v-model="organizationsQueryError"
        variant="danger"
        dismissible
        class="mt-3 mb-0"
      >
        {{ organizationsQueryError }}
        <b-button
          @click="requestRefetchOrganizations"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Filter -->
      <div class="my-2">
        <BForm
          class="d-flex flex-row align-items-end flex-wrap gap-2"
          @submit.prevent="onSearchRequested"
        >
          <BFormGroup label="Organization name" label-for="org-name">
            <BFormInput
              id="org-name"
              type="text"
              v-model="namePattern"
              placeholder="e.g. Sampling Human"
            />
          </BFormGroup>
          <!-- Filter action buttons -->
          <BButton variant="outline-secondary" @click="clearFilter">
            <font-awesome-icon icon="trash" class="me-1" />
            Clear filter
          </BButton>
          <BButton
            variant="outline-dark"
            type="submit"
            :disabled="organizationsQueryInProgress"
          >
            <span v-if="organizationsQueryInProgress">
              <b-spinner small label=""></b-spinner>
              Searching
            </span>
            <span v-else>
              <font-awesome-icon icon="magnifying-glass" class="me-1" />
              Refresh
            </span>
          </BButton>
        </BForm>
      </div>

      <!-- Paging (above) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />

      <!-- List of Organizations -->
      <div v-if="!organizations.length">
        <h5 class="my-5 py-5 text-center">There are no organizations.</h5>
      </div>
      <BCard v-else>
        <b-table
          small
          striped
          responsive
          hover
          :items="organizations"
          :fields="fields"
          :busy="organizationsQueryInProgress"
          class="browser-table"
          @row-clicked="orgDetailRequested"
        >
          <!-- Default data cell scoped slot -->
          <template #cell(name)="data">
            <strong>{{ data.value }}</strong>
          </template>
          <template #cell(ownerFullName)="data">
            {{ organizationOwnerFullName(data.item) }}
          </template>
          <template #cell(ownerEmail)="data">
            {{ organizationOwnerEmail(data.item) }}
          </template>
          <!-- Project detail button -->
          <template #cell(id)="data">
            <BButtonGroup>
              <BButton
                variant="outline-secondary"
                class="btn-sm"
                @click="orgDetailRequested(data.item)"
              >
                <font-awesome-icon :icon="['fa', 'magnifying-glass']" />
              </BButton>
              <BButton
                variant="outline-secondary"
                class="btn-sm"
                @click="orgUpdateRequested(data.item)"
              >
                <font-awesome-icon :icon="['fa', 'pencil']" />
              </BButton>
            </BButtonGroup>
          </template>
        </b-table>
      </BCard>
    </div>
    <BModal
      v-if="selectedOrg"
      v-model="showModalOrgDetail"
      @close="closeModals"
      title="Organization detail"
    >
      <OrganizationForm
        :organization="selectedOrg"
        :headerText="''"
        :editable="false"
        :showAdminFields="userStore.admin"
      />
      <template #footer>
        <b-button variant="outline-dark" @click="closeModals">Close</b-button>
      </template>
    </BModal>
    <BModal
      v-if="selectedOrg"
      v-model="showModalOrgUpdate"
      @close="closeModals"
      title="Update organization"
    >
      <OrganizationForm
        :organization="selectedOrg"
        :headerText="''"
        :editable="true"
        :buttonText="'Update organization'"
        :showAdminFields="userStore.admin"
        @organizationUpdated="onOrganizationUpdated"
      />
      <template #footer>
        <b-button variant="outline-dark" @click="closeModals">Close</b-button>
      </template>
    </BModal>
  </div>
</template>

<style scoped></style>
